import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { BookOpen, Brain, Target, Timer, CheckCircle2, Flame } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface WeeklyGoal {
  id: string;
  goal_type: string;
  target_value: number;
  current_value: number;
  completed: boolean;
}

interface WeeklyGoalsSetupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingGoals: WeeklyGoal[];
  weekStart: string;
  onSave: () => void;
}

const goalTypes = [
  { type: 'questions', label: 'Questões respondidas', icon: BookOpen, defaultValue: 50 },
  { type: 'correct_answers', label: 'Questões corretas', icon: CheckCircle2, defaultValue: 30 },
  { type: 'simulations', label: 'Simulados completos', icon: Brain, defaultValue: 3 },
  { type: 'flashcards', label: 'Flashcards revisados', icon: Target, defaultValue: 50 },
  { type: 'study_time', label: 'Pomodoros completos', icon: Timer, defaultValue: 10 },
  { type: 'streak', label: 'Dias de streak', icon: Flame, defaultValue: 5 },
];

export function WeeklyGoalsSetup({
  open,
  onOpenChange,
  existingGoals,
  weekStart,
  onSave,
}: WeeklyGoalsSetupProps) {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Record<string, { enabled: boolean; target: number }>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const initialGoals: Record<string, { enabled: boolean; target: number }> = {};
    
    goalTypes.forEach(({ type, defaultValue }) => {
      const existing = existingGoals.find(g => g.goal_type === type);
      initialGoals[type] = {
        enabled: !!existing,
        target: existing?.target_value || defaultValue,
      };
    });
    
    setGoals(initialGoals);
  }, [existingGoals, open]);

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    
    try {
      // Delete existing goals for this week
      await supabase
        .from('user_weekly_goals')
        .delete()
        .eq('user_id', user.id)
        .eq('week_start', weekStart);

      // Insert new goals
      const newGoals = Object.entries(goals)
        .filter(([_, value]) => value.enabled && value.target > 0)
        .map(([type, value]) => ({
          user_id: user.id,
          week_start: weekStart,
          goal_type: type,
          target_value: value.target,
          current_value: existingGoals.find(g => g.goal_type === type)?.current_value || 0,
          completed: false,
        }));

      if (newGoals.length > 0) {
        const { error } = await supabase
          .from('user_weekly_goals')
          .insert(newGoals);

        if (error) throw error;
      }

      toast.success('Metas salvas com sucesso!');
      onSave();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving goals:', error);
      toast.error('Erro ao salvar metas');
    } finally {
      setSaving(false);
    }
  };

  const toggleGoal = (type: string) => {
    setGoals(prev => ({
      ...prev,
      [type]: { ...prev[type], enabled: !prev[type]?.enabled },
    }));
  };

  const updateTarget = (type: string, target: number) => {
    setGoals(prev => ({
      ...prev,
      [type]: { ...prev[type], target: Math.max(1, target) },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Definir Metas Semanais</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {goalTypes.map(({ type, label, icon: Icon, defaultValue }) => (
            <div
              key={type}
              className="flex items-center justify-between p-3 rounded-lg border"
            >
              <div className="flex items-center gap-3">
                <Switch
                  checked={goals[type]?.enabled || false}
                  onCheckedChange={() => toggleGoal(type)}
                />
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <Label className="font-normal">{label}</Label>
                </div>
              </div>
              
              {goals[type]?.enabled && (
                <Input
                  type="number"
                  min={1}
                  value={goals[type]?.target || defaultValue}
                  onChange={(e) => updateTarget(type, parseInt(e.target.value) || 1)}
                  className="w-20 text-center"
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar Metas'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
